import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, X, Sparkles } from "lucide-react";
import { toast, Toaster } from "sonner";

const TYPES = ["text", "textarea", "number", "date", "select", "toggle"];
const blank = { label: "", key: "", type: "text", options: [], required: false, order_index: 0, active: true };

export default function AdminDRSFields() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [f, setF] = useState(blank);
    const [optsText, setOptsText] = useState("");

    const load = () => api.get("/admin/drs-fields").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!f.label || !f.key) return toast.error("Label & key required");
        const body = { ...f, options: optsText.split(",").map(s => s.trim()).filter(Boolean) };
        try { editing === "new" ? await api.post("/admin/drs-fields", body) : await api.put(`/admin/drs-fields/${editing}`, body); toast.success("Saved"); setEditing(null); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/drs-fields/${id}`); load(); };

    const seedDefaults = async () => {
        if (!window.confirm("Add the comprehensive default DRS fields? Existing fields with the same key will be skipped.")) return;
        try {
            const { data } = await api.post("/admin/drs-fields/seed-defaults");
            toast.success(`Added ${data.inserted} field${data.inserted === 1 ? "" : "s"} (${data.skipped} skipped, already present)`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    };

    return (
        <div data-testid="admin-drs-fields">
            <Toaster position="top-center" />
            <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
                <div><div className="label-eyebrow text-sky-700">Field log</div><h1 className="font-serif text-4xl text-stone-900 mt-2">DRS fields</h1></div>
                <div className="flex gap-2">
                    <Button onClick={seedDefaults} variant="outline" className="rounded-none border-sky-700 text-sky-700 hover:bg-sky-50" data-testid="seed-drs-defaults"><Sparkles className="w-4 h-4 mr-2" />Add default set</Button>
                    <Button onClick={() => { setEditing("new"); setF(blank); setOptsText(""); }} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-drs"><Plus className="w-4 h-4 mr-2" />New field</Button>
                </div>
            </div>

            {editing && (
                <div className="bg-white border border-stone-200 p-5 mb-6 space-y-3">
                    <div className="flex justify-between"><h2 className="font-serif text-2xl">{editing === "new" ? "New" : "Edit"} field</h2><Button variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button></div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Label" className="rounded-none border-stone-300" data-testid="drs-label" />
                        <Input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="key (snake_case)" className="rounded-none border-stone-300" data-testid="drs-key" />
                        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                            <SelectTrigger className="rounded-none border-stone-300"><SelectValue /></SelectTrigger>
                            <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" value={f.order_index} onChange={(e) => setF({ ...f, order_index: parseInt(e.target.value || 0) })} placeholder="Order index" className="rounded-none border-stone-300" />
                    </div>
                    {f.type === "select" && <Input value={optsText} onChange={(e) => setOptsText(e.target.value)} placeholder="Options (comma-separated)" className="rounded-none border-stone-300" />}
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Required</span><Switch checked={f.required} onCheckedChange={(v) => setF({ ...f, required: v })} /></label>
                        <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Active</span><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></label>
                    </div>
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="drs-save">Save</Button>
                </div>
            )}

            <div className="bg-white border border-stone-200">
                {items.map(d => (
                    <div key={d.id} className="border-b border-stone-100 last:border-b-0 p-4 grid grid-cols-12 gap-3 items-center" data-testid={`drs-row-${d.id}`}>
                        <div className="col-span-4 font-medium">{d.label}{d.required && <span className="text-red-500 ml-1">*</span>}</div>
                        <div className="col-span-3 font-mono text-xs">{d.key}</div>
                        <div className="col-span-2 text-xs uppercase tracking-widest">{d.type}</div>
                        <div className="col-span-1 text-xs">{d.active ? "Active" : "Hidden"}</div>
                        <div className="col-span-2 flex justify-end gap-1">
                            <button onClick={() => { setEditing(d.id); setF(d); setOptsText((d.options || []).join(", ")); }} className="text-stone-700 underline text-xs">Edit</button>
                            <button onClick={() => del(d.id)} className="text-red-700"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No fields.</div>}
            </div>
        </div>
    );
}
